import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { openLink } from '@tma.js/sdk-react';
import { ExternalLink, Minus, Trophy, XCircle } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { Loader } from '@/components/common/Loader';
import { ChessStatusBadge } from '@/components/chess/ChessStatusBadge';
import { ChessReplay } from '@/components/chess/ChessReplay';
import { getMyLogtoSub } from '@/lib/logto';
import { useGetChessChallengeQuery } from '@/services/qchessApi';
import { classifyQchessError, qchessErrorKey } from '@/services/qchessErrors';
import { cn } from '@/lib/cn';

const TERMINAL = new Set(['completed', 'declined', 'expired', 'aborted']);

/**
 * Chess duel detail / result screen. Reached by tapping any challenge in the
 * duels list (always available for completed duels). For a completed duel it
 * shows the outcome (won / lost / draw + score) and a Lichess replay embed of
 * each played game; for an in-progress duel it shows the live game (open +
 * board). It polls while the duel is live and refetches when the user returns
 * to the mini app (e.g. after playing on lichess.org), so the result + replay
 * appear automatically the moment the game ends — no manual refresh.
 */
export function ChessChallengeDetail() {
  const { t } = useTranslation('chess');
  const haptics = useHaptics();
  const { id } = useParams<{ id: string }>();
  const me = getMyLogtoSub();

  const [pollMs, setPollMs] = useState(5000);
  const {
    data: ch,
    isLoading,
    error,
    refetch,
  } = useGetChessChallengeQuery(id ?? '', {
    skip: !id,
    refetchOnMountOrArgChange: true,
    pollingInterval: pollMs,
  });

  // Stop polling once the duel reaches a terminal state.
  useEffect(() => {
    setPollMs(ch && TERMINAL.has(ch.status) ? 0 : 5000);
  }, [ch]);

  // Refetch when the user returns to the mini app (e.g. after playing the game
  // on lichess.org in the system browser) so the result shows immediately.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void refetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refetch]);

  if (isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (error || !ch) {
    return (
      <div className="px-4 pb-6 pt-5">
        <p className="rounded-md bg-danger/15 px-3 py-2 text-sm text-danger" role="alert">
          {error
            ? t(qchessErrorKey(classifyQchessError(error)))
            : t('challenges.detail.notFound')}
        </p>
      </div>
    );
  }

  const isChallenger = !!me && ch.challengerLogtoUserId === me;
  const opponentName = isChallenger ? ch.opponentUsername : ch.challengerUsername;
  const myLichessUsername = isChallenger ? ch.challengerUsername : ch.opponentUsername;
  const myWins = isChallenger ? ch.winsChallenger : ch.winsOpponent;
  const theirWins = isChallenger ? ch.winsOpponent : ch.winsChallenger;

  const outcome =
    ch.status !== 'completed'
      ? null
      : ch.result?.winnerLogtoUserId == null
        ? 'draw'
        : me && ch.result.winnerLogtoUserId === me
          ? 'won'
          : 'lost';

  // Completed duels replay their played games; an in-progress duel embeds the
  // current live game. `gameIds` carries the full series (bo3/bo5 → multiple).
  const replayIds =
    ch.gameIds && ch.gameIds.length > 0
      ? ch.gameIds
      : ch.currentGameId
        ? [ch.currentGameId]
        : [];

  return (
    <div className="flex flex-col gap-4 px-4 pb-6 pt-5">
      <header className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          <h1 className="min-w-0 truncate text-2xl font-bold text-fg">
            <span className="text-fg-muted">{t('challenges.vs')} </span>
            {opponentName}
          </h1>
          <ChessStatusBadge status={ch.status} variant="challenge" />
        </div>
        <p className="text-sm text-fg-muted">
          <span className="uppercase">{t(`format.${ch.format}`)}</span>
          <span aria-hidden> · </span>
          {t('challenges.score', { a: myWins, b: theirWins })}
        </p>
      </header>

      {/* Result banner (completed duels) */}
      {outcome && (
        <section
          className={cn(
            'flex items-center gap-3 rounded-xl p-4',
            outcome === 'won'
              ? 'bg-success/15 text-success'
              : outcome === 'lost'
                ? 'bg-danger/15 text-danger'
                : 'bg-bg-tertiary text-fg-muted',
          )}
        >
          {outcome === 'won' ? (
            <Trophy aria-hidden className="h-6 w-6 shrink-0" />
          ) : outcome === 'lost' ? (
            <XCircle aria-hidden className="h-6 w-6 shrink-0" />
          ) : (
            <Minus aria-hidden className="h-6 w-6 shrink-0" />
          )}
          <div className="min-w-0">
            <p className="text-base font-bold">{t(`challenges.detail.${outcome}`)}</p>
            <p className="text-[11px] opacity-80">
              {t('challenges.score', { a: myWins, b: theirWins })}
            </p>
          </div>
        </section>
      )}

      {/* Pending / terminal-non-result states */}
      {ch.status === 'pending' && (
        <p className="rounded-xl bg-bg-secondary px-4 py-3 text-sm text-fg-muted">
          {t('challenges.detail.waiting')}
        </p>
      )}
      {(ch.status === 'aborted' ||
        ch.status === 'declined' ||
        ch.status === 'expired') && (
        <p className="rounded-xl bg-bg-secondary px-4 py-3 text-sm text-fg-muted">
          {t(`challenges.detail.${ch.status}`)}
        </p>
      )}

      {/* Open the live game on Lichess (in progress only) */}
      {ch.status === 'in_progress' && ch.currentGameId && (
        <button
          type="button"
          onClick={() => {
            haptics.impact('medium');
            openLink.ifAvailable(`https://lichess.org/${ch.currentGameId}`);
          }}
          className="flex items-center justify-center gap-2 rounded-xl bg-brand px-4 py-3 text-sm font-bold uppercase tracking-wide text-brand-fg transition-opacity active:opacity-80"
        >
          <ExternalLink aria-hidden className="h-4 w-4" />
          {t('challenges.openGame')}
        </button>
      )}

      {/* Board (in progress) / replay (completed) — Lichess embed is view-only;
          for a finished game it renders the move-by-move replay controls. */}
      {replayIds.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
            {ch.status === 'in_progress'
              ? t('challenges.detail.board')
              : t('challenges.detail.replay')}
          </h2>
          {replayIds.map((gid, i) => (
            <div key={gid} className="flex flex-col gap-1">
              {replayIds.length > 1 && (
                <span className="text-[11px] text-fg-muted">
                  {t('challenges.detail.game', { n: i + 1 })}
                </span>
              )}
              {ch.status === 'in_progress' ? (
                <div className="overflow-hidden rounded-lg border border-border">
                  <iframe
                    title={t('challenges.detail.board')}
                    src={`https://lichess.org/embed/${encodeURIComponent(gid)}?theme=auto&bg=dark`}
                    className="h-[420px] w-full"
                    loading="lazy"
                    sandbox="allow-scripts allow-same-origin allow-popups"
                  />
                </div>
              ) : (
                <ChessReplay gameId={gid} myUsername={myLichessUsername} />
              )}
            </div>
          ))}
        </section>
      ) : (
        ch.status === 'completed' && (
          <p className="text-sm text-fg-muted">{t('challenges.detail.noGames')}</p>
        )
      )}

      <p className="px-1 text-[11px] leading-snug text-fg-muted">{t('disclaimer')}</p>
    </div>
  );
}
